import { useCallback, useState } from "react";
import { useSubscription } from "@apollo/client/react";
import { OperationProgressDocument, type OperationProgressFieldsFragment } from "@/graphql/generated/operations";

// Follows one long-running operation through genesis' operationProgress side channel
// (@adr SPARK-0047-operation-progress-renders-through-one-step-chain@1). The caller starts a run with
// begin(), hands the returned id to the operation, and calls end() once the operation settled: the
// operation's own result is the truth, this stream only tells how far along it is and may end without
// a final snapshot. Every begin() is a fresh id, so a late snapshot of an earlier run can never land
// on the current one.
export type OperationProgress = OperationProgressFieldsFragment;

export function useOperationProgress() {
  const [progressId, setProgressId] = useState<string | null>(null);
  const [progress, setProgress] = useState<OperationProgress | null>(null);

  useSubscription(OperationProgressDocument, {
    variables: { progressId: progressId ?? "" },
    skip: !progressId,
    // Progress is transient; it has no business in the normalized cache.
    fetchPolicy: "no-cache",
    onData: ({ data }) => {
      const next = data.data?.operationProgress;
      if (!next) return;
      setProgress((current) => {
        if (next.progressId !== progressId) return current;
        if (current && current.progressId === next.progressId && current.revision >= next.revision) return current;
        return next;
      });
    },
  });

  const begin = useCallback(() => {
    const id = crypto.randomUUID();
    setProgress(null);
    setProgressId(id);
    return id;
  }, []);

  // With an id, only that run ends: a run that settles after a newer one started must not switch off
  // the newer one's progress. Without an id, whatever is running ends (the editor moved on).
  const end = useCallback((runId?: string) => {
    setProgressId((current) => (runId && current !== runId ? current : null));
    setProgress((current) => (runId && current && current.progressId !== runId ? current : null));
  }, []);

  return { progressId, progress, begin, end };
}
